import { keys, map, values } from 'lodash-es'
import { feedsAPI } from '#app/lib/api/feeds'
import { assertString } from '#app/lib/assert_types'
import { getColorHexCodeFromCouchUuId, getColorSquareDataUri } from '#app/lib/images/images'
import { normalizeStringWithFolding } from '#app/lib/strings'
import { treq, treqPartial } from '#app/lib/treq'
import { getVisibilitySummary, getVisibilitySummaryLabel, visibilitySummariesData, type VisibilitySummaryData, type VisibilitySummaryLabel } from '#general/lib/visibility'
import type { SerializedItem } from '#inventory/lib/items'
import type { ColorHexCode, RelativeUrl } from '#server/types/common'
import type { ControllersAPI } from '#server/types/controllers_api'
import type { ImageDataUrl } from '#server/types/image'
import type { ItemId } from '#server/types/item'
import type { PublicizableShelf, Shelf, ShelfId } from '#server/types/shelf'
import type { UserId } from '#server/types/user'
import { mainUser } from '#user/lib/main_user'

export async function getShelfById (id: ShelfId) {
  const { shelves } = await treq('shelves', 'get', 'by-ids', { ids: [ id ] })
  return serializeShelf(shelves[id])
}

export async function getShelvesByIds (ids: ShelfId[]) {
  if (ids.length === 0) return []
  const { shelves } = await treq('shelves', 'get', 'by-ids', { ids })
  return values(shelves).sort(byName).map(serializeShelf)
}

export async function createShelf (params: ControllersAPI['shelves']['post']['default']['params']) {
  const { shelf } = await treq('shelves', 'post', 'default', params)
  return serializeShelf(shelf)
}

export async function updateShelf (params: ControllersAPI['shelves']['put']['default']['params']) {
  try {
    const { shelf } = await treq('shelves', 'put', 'default', params)
    return serializeShelf(shelf)
  } catch (err) {
    if (err.message !== 'nothing to update') {
      throw err
    }
  }
}

export const deleteShelf = treqPartial('shelves', 'post', 'delete')

interface AddOrRemoveShelfParams {
  shelfId: ShelfId
  items: SerializedItem[]
}

export function removeItemsFromShelf ({ shelfId, items }: AddOrRemoveShelfParams) {
  const itemsIds = map(items, '_id')
  return shelfActionReq(shelfId, itemsIds, 'remove-items')
}

export function addItemsToShelf ({ shelfId, items }: AddOrRemoveShelfParams) {
  const itemsIds = map(items, '_id')
  return shelfActionReq(shelfId, itemsIds, 'add-items')
}

interface AddItemsByIdsToShelfParams {
  shelfId: ShelfId
  itemsIds: ItemId[]
}

export async function addItemsByIdsToShelf ({ shelfId, itemsIds }: AddItemsByIdsToShelfParams) {
  return shelfActionReq(shelfId, itemsIds, 'add-items')
}

export async function getShelvesByOwner (userId: UserId) {
  assertString(userId)
  const { shelves } = await treq('shelves', 'get', 'by-owners', { owners: [ userId ] })
  return values(shelves).sort(byName).map(serializeShelf)
}

export async function countShelves (userId: UserId) {
  const { shelves } = await treq('shelves', 'get', 'by-owners', { owners: [ userId ] })
  return keys(shelves).length
}

async function shelfActionReq (id: ShelfId, itemsIds: ItemId[], action: 'add-items' | 'remove-items') {
  const { shelves } = await treq('shelves', 'put', action, { id, items: itemsIds })
  return serializeShelf(shelves[id])
}

export type SerializedShelf = (Shelf | PublicizableShelf) & {
  pathname: RelativeUrl
  picture: ImageDataUrl
  isEditable: boolean
  iconData?: VisibilitySummaryData
  iconLabel?: VisibilitySummaryLabel
  matcheableText: string
}

export function serializeShelf (shelf: Shelf | PublicizableShelf) {
  const { _id } = shelf
  let { color } = shelf
  color ??= `#${getColorHexCodeFromCouchUuId(_id)}` as ColorHexCode
  const pathname = `/shelves/${_id}`
  const picture = getColorSquareDataUri(color)
  const isEditable = shelf.owner === mainUser?._id
  let iconData, iconLabel
  if ('visibility' in shelf) {
    const { visibility } = shelf
    const visibilitySummary = getVisibilitySummary(visibility)
    iconData = visibilitySummariesData[visibilitySummary]
    iconLabel = getVisibilitySummaryLabel(visibility)
  }
  return {
    ...shelf,
    color,
    pathname,
    picture,
    isEditable,
    iconData,
    iconLabel,
    matcheableText: normalizeStringWithFolding(`${shelf.name} ${shelf.description || ''}`),
  } as SerializedShelf
}

export function getShelfMetadata (shelf: Shelf | PublicizableShelf) {
  const { _id: shelfId, name, description, picture, pathname } = serializeShelf(shelf)
  return {
    title: name,
    description,
    image: picture,
    url: pathname,
    rss: feedsAPI('shelf', shelfId),
  }
}

const byName = (a, b) => normalizeName(a.name) > normalizeName(b.name) ? 1 : -1

const normalizeName = name => name.toLowerCase()

export function shelfMatchesFilter (shelf: SerializedShelf, textFilter: string) {
  if (textFilter) {
    return shelf.matcheableText.includes(textFilter)
  } else {
    return shelf
  }
}
